// src/components/modals/PublishFolderModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button, Form, InputGroup, Alert, Badge, Spinner } from "react-bootstrap";
import axios from "axios";

export default function PublishFolderModal({ show, onHide, bucket, prefix }) {
  const apiToken = localStorage.getItem("token");

  const [expiration, setExpiration] = useState(24);
  const [unit, setUnit] = useState("h");
  const [neverExpire, setNeverExpire] = useState(false);

  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [error, setError] = useState(null);

  // 🔄 Reset sempre que abrir ou mudar a pasta
  useEffect(() => {
    if (!show) return;
    setExpiration(24);
    setUnit("h");
    setNeverExpire(false);
    setShareUrl(null);
    setError(null);
    setLoading(false);
  }, [show, bucket, prefix]);

  async function handlePublish() {
    setError(null);
    setLoading(true);

    try {
      const payload = {
        bucket,
        prefix,
        expiration: neverExpire ? 0 : expiration,
        unit: neverExpire ? null : unit,
      };

      const { data } = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/share/folder`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${apiToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      setShareUrl(data.shareUrl);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Erro ao gerar link");
    } finally {
      setLoading(false);
    }
  }

  function toggleNeverExpire() {
    setNeverExpire((v) => !v);
    if (!neverExpire) setExpiration(0);
    else {
      setExpiration(24);
      setUnit("h");
    }
  }

  function copyLink() {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>📤 Compartilhar Pasta</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* INFO DA PASTA */}
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: 14,
            marginBottom: 20,
            fontSize: 14,
          }}
        >
          <div>
            <strong>Bucket:</strong>{" "}
            <Badge bg="secondary">{bucket}</Badge>
          </div>
          <div style={{ marginTop: 6, wordBreak: "break-all" }}>
            <strong>Pasta:</strong> {prefix}
          </div>
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {/* =======================
            ESTADO: AINDA NÃO GEROU
           ======================= */}
        {!shareUrl && (
          <>
            <Form.Check
              type="switch"
              id="never-expire"
              label="Link sem expiração"
              checked={neverExpire}
              onChange={toggleNeverExpire}
              className="mb-3"
            />

            {!neverExpire && (
              <>
                <Form.Label>Expiração do link</Form.Label>
                <InputGroup className="mb-4">
                  <Form.Control
                    type="number"
                    min={1}
                    value={expiration}
                    onChange={(e) => setExpiration(Number(e.target.value))}
                  />
                  <Form.Select
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                  >
                    <option value="h">Horas</option>
                    <option value="d">Dias</option>
                  </Form.Select>
                </InputGroup>
              </>
            )}

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={onHide}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handlePublish}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Gerando…
                  </>
                ) : (
                  "Gerar link público"
                )}
              </Button>
            </div>
          </>
        )}

        {/* =======================
            ESTADO: LINK GERADO
           ======================= */}
        {shareUrl && (
          <>
            <Alert variant="success">
              ✅ Link público gerado com sucesso
            </Alert>

            <Form.Label>Link de acesso</Form.Label>
            <InputGroup className="mb-3">
              <Form.Control readOnly value={shareUrl} />
              <Button variant="outline-secondary" onClick={copyLink}>
                Copiar
              </Button>
            </InputGroup>

            <div className="d-flex justify-content-between align-items-center">
              <div style={{ fontSize: 13, opacity: 0.7 }}>
                Qualquer pessoa com o link poderá acessar esta pasta
              </div>

              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  onClick={() => window.open(shareUrl, "_blank", "noopener")}
                >
                  Abrir link
                </Button>
                <Button variant="secondary" onClick={onHide}>
                  Fechar
                </Button>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
